class Quiz extends HTMLElement {
  constructor() {
    super();

    this.fullPriceUrl = this.dataset.redirectUrl ?? false;
    this.jcPdpSbUrl = this.dataset.redirectUrlPdpSb ?? false;
    this.jcPdpLbUrl = this.dataset.redirectUrlPdpLb ?? false;
    this.fetchUrl = this.dataset.gcloudUrl ?? false;
    this.defaultRedirect = this.dataset.defaultRedirect ?? 'full_price';
    this.klaviyoListID = this.dataset.klaviyoListId ?? false;
    this.offerLandingVisited = JSON.parse(sessionStorage.getItem('offerLandingVisited'));
    this.quizSkipped = JSON.parse(sessionStorage.getItem('quizSkipped'));

    // Built-in offer settings
    this.enableBuiltInOffer = this.dataset.enableBuiltInOffer === 'true';
    this.quizName = this.dataset.quizName || 'Quiz';
    this.quizTag = this.dataset.quizTag || false;

    this.quizInputs = {};
    this.setUpEventListeners();
  }

  applyWeightToDOMFromQuizInputs() {
    // When returning from checkout, the range input can reset to its default (often 25),
    // even though `quiz_data` in localStorage contains the selected weight.
    const rangeWeight = this.querySelector('input[name="weight"][type="range"]');
    if (!rangeWeight) return;

    const saved = this.quizInputs?.weight;
    if (saved == null || saved === '') return;

    const savedNum = parseInt(String(saved), 10);
    if (Number.isNaN(savedNum)) return;

    rangeWeight.value = String(savedNum);
    this.updateSliderProgress(rangeWeight);
  }

  syncWeightFromDOM() {
    // Keep in-memory state aligned with UI for history/back/forward flows.
    const rangeWeight = this.querySelector('input[name="weight"][type="range"]');
    if (rangeWeight && rangeWeight.value != null && rangeWeight.value !== '') {
      // Important: on return from checkout, the slider may reset to its default value.
      // If we already have a persisted weight from localStorage, don't overwrite it with the default.
      try {
        const step = new URLSearchParams(window.location.search).get('step');
        const domValue = String(rangeWeight.value);
        const domDefault = rangeWeight.dataset?.default != null ? String(rangeWeight.dataset.default) : null;
        const existing = this.quizInputs?.weight != null ? String(this.quizInputs.weight) : '';

        const domLooksDefault = domDefault != null && domValue === domDefault;
        const existingLooksNonDefault = existing !== '' && (domDefault == null || existing !== domDefault);

        if ((step === 'offer' || step === 'last') && domLooksDefault && existingLooksNonDefault) {
          return; // keep persisted weight
        }
      } catch (e) {
        // ignore URL parsing issues; fall through to normal behavior
      }

      this.quizInputs.weight = String(rangeWeight.value);
      return;
    }

    const checkedWeight = this.querySelector('input[name="weight"]:checked');
    if (checkedWeight && checkedWeight.value != null && checkedWeight.value !== '') {
      this.quizInputs.weight = String(checkedWeight.value);
    }
  }

  getOfferBlockMatchingIndex(offerBlock) {
    // Always compute from current weight + offer ranges (avoid stale dataset index).
    this.syncWeightFromDOM();
    const weight = parseInt(this.quizInputs.weight, 10);
    const weightRanges = (offerBlock?.dataset?.weightRanges || '').split(',').filter(Boolean);

    if (weightRanges.length === 0 || isNaN(weight)) return 0;

    for (let i = 0; i < weightRanges.length; i++) {
      if (this.isWeightInRange(weight, weightRanges[i])) return i;
    }

    return 0;
  }

  setUpEventListeners() {
    document.addEventListener('DOMContentLoaded', this.onInit.bind(this));

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:load', this.onSectionLoad.bind(this));
    }

    this.querySelectorAll("input:not([type='submit'])").forEach((input) => {
      if (input.type === 'radio') {
        input.addEventListener('click', this.onTabChange.bind(this));
      } else if (input.type === 'range') {
        input.addEventListener('input', this.onRangeInputChange.bind(this));
        window.addEventListener('resize', () => this.updateSliderProgress(input));
      } else {
        input.addEventListener('input', this.onInputChange.bind(this));

        if (input.type !== 'email') {
          input.addEventListener('keypress', this.onInputKeyPress.bind(this));
        }
      }
    });

    this.querySelectorAll("input[name='breed']").forEach((breedInput) => {
      breedInput.addEventListener('click', this.onBreedInputClick.bind(this));
      breedInput.addEventListener('blur', this.onBreedInputBlur.bind(this));
    });

    this.querySelectorAll('ul[data-breed-list] li').forEach((list) =>
      list.addEventListener('click', this.onBreedSelect.bind(this))
    );

    this.querySelectorAll('form').forEach((form) => {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    });

    this.querySelectorAll('quiz-back').forEach((backBtn) =>
      backBtn.addEventListener('click', this.onBackClick.bind(this))
    );
  }

  setupQuizInputsFromLocalStorage(quizData) {
    if (quizData) {
      try {
        this.quizInputs = JSON.parse(quizData);
      } catch (error) {
        console.error('Failed to parse quiz_data from localStorage:', error);
        this.quizInputs = {}; // Fallback to an empty object
      }
    }
  }

  onInit() {
    const quizData = localStorage.getItem('quiz_data');
    const quizSteps = document.querySelectorAll('quiz-step');
    const quizLastStepIndex = quizSteps.length - 2;

    const quizFirstStep = this.querySelector("quiz-step[quiz-step-index='0']");
    const quizLastStep = this.querySelector("quiz-step[quiz-step-index='" + quizLastStepIndex + "']");
    const quizTextInputs = this.querySelectorAll("input[type='text'], input[type='number'], input[type='email']");

    // Re-enable CTA buttons on page load (for browser back button from checkout)
    this.reEnableOfferCTAButtons();

    window.onpopstate = (event) => {
      this.onBackClick();
    };

    setTimeout(() => {
      quizTextInputs.forEach((input) => {
        const thisInputBtn = input.closest('quiz-step');

        this.toggleSubmitButton(thisInputBtn, input.value.trim() !== '');
      });

      // Initialize range sliders
      this.initializeSliders();
    }, 500);

    // Check URL for current step (for browser back button from checkout)
    const urlParams = new URLSearchParams(window.location.search);
    const urlStep = urlParams.get('step');

    if (urlStep === 'offer' && this.enableBuiltInOffer && quizData) {
      // Returning to offer step from checkout
      quizFirstStep.style.display = 'none';
      this.setupQuizInputsFromLocalStorage(quizData);
      this.applyWeightToDOMFromQuizInputs();

      const offerStep = this.querySelector('quiz-step[quiz-step-index="offer"]');
      if (offerStep) {
        offerStep.style.display = 'flex';

        // Re-process to find and show the correct offer
        this.processBuiltInOfferOnReturn();
      }
    } else if (urlStep === 'last' && quizData) {
      // Returning to loading step
      quizFirstStep.style.display = 'none';
      this.setupQuizInputsFromLocalStorage(quizData);
      this.applyWeightToDOMFromQuizInputs();

      const lastStep = this.querySelector("quiz-step[quiz-step-index='last']");
      if (lastStep) {
        lastStep.style.display = 'flex';
        // Reset loading progress
        const progress = lastStep.querySelector('[loading-screen-progress]');
        if (progress) {
          progress.style.width = '0%';
        }
      }
    } else if (!this.quizSkipped && this.offerLandingVisited && quizData) {
      quizFirstStep.style.display = 'none';
      quizLastStep.style.display = 'flex';
      this.setupQuizInputsFromLocalStorage(quizData);
      this.applyWeightToDOMFromQuizInputs();
      this.animateElementBlocks(quizLastStep);
      this.animateProgressBar(quizLastStepIndex);
      this.updateURLParam('step', quizLastStepIndex);
    } else {
      this.animateElementBlocks(quizFirstStep);
      this.updateURLParam('step', 0);
    }
  }

  updateURLParam(key, value) {
    const url = new URL(window.location);
    url.searchParams.set(key, value);
    window.history.replaceState({}, '', url);
  }

  onSectionLoad() {
    this.querySelectorAll('quiz-step').forEach((step) => {
      step.style.display = 'block';

      step.querySelectorAll('[animate-fade-in]').forEach((element) => {
        element.removeAttribute('animate-fade-in');
      });
    });
  }

  onBreedInputClick(event) {
    const breedDropdown = event.target.parentElement.querySelector('ul[data-breed-list]');
    breedDropdown.style.display = 'block';
    clearTimeout(this.hideTimeout);
  }

  onBreedInputBlur(event) {
    const breedDropdown = event.target.parentElement.querySelector('ul[data-breed-list]');

    this.hideTimeout = setTimeout(() => {
      const selectedBreed = breedDropdown.querySelector('li[selected]');

      event.target.value =
        event.target.value === '' || selectedBreed === null ? 'Not sure / Not listed' : selectedBreed.textContent;

      breedDropdown.style.display = 'none';

      this.toggleSubmitButton(this.getCurrentStep(event), true);
    }, 200);
  }

  onBreedSelect(event) {
    const thisBreed = event.target.textContent;
    const thisInput = event.target.parentElement.previousElementSibling;
    const breedLists = event.target.parentElement.querySelectorAll('li');

    breedLists.forEach((list) => list.removeAttribute('selected'));
    event.target.setAttribute('selected', true);

    thisInput.value = thisBreed;
  }

  filterDropdown(event) {
    const dropDown = event.target.nextElementSibling;

    if (dropDown !== null && dropDown.hasAttribute('data-breed-list')) {
      const inputValue = event.target.value.toLowerCase();
      let firstItemFound = false;

      if (!dropDown.originalItems) {
        dropDown.originalItems = Array.from(dropDown.querySelectorAll('li'));
      }

      dropDown.innerHTML = '';

      dropDown.originalItems.forEach((li) => {
        const listItemText = li.textContent;
        const lowerCaseListItemText = listItemText.toLowerCase();

        if (lowerCaseListItemText.includes(inputValue)) {
          const boldedText = listItemText.replace(new RegExp(inputValue, 'gi'), (match) => `<strong>${match}</strong>`);
          li.innerHTML = boldedText;

          if (!firstItemFound) {
            li.setAttribute('selected', 'true');
            firstItemFound = true;
          } else {
            li.removeAttribute('selected');
          }

          dropDown.appendChild(li);
        }
      });
    }
  }

  getCurrentStep(event) {
    return event.target.closest('quiz-step');
  }

  onTabChange(event) {
    const currentStep = this.getCurrentStep(event);
    const submitButton = currentStep.querySelector("input[type='submit']");
    const labels = currentStep.querySelectorAll('label');

    if (submitButton === null) {
      labels.forEach((label) => label.toggleAttribute('disabled', true));

      setTimeout(() => {
        this.onFormSubmit(event);
        labels.forEach((label) => label.toggleAttribute('disabled', false));
      }, 600);
    } else {
      this.toggleSubmitButton(this.getCurrentStep(event), true);
    }
  }

  onInputChange(event) {
    const isEmpty = event.target.value.trim() === '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmail = event.target.type === 'email';
    const isValid = isEmail ? emailRegex.test(event.target.value) : !isEmpty;

    event.target.name === 'breed' ? this.filterDropdown(event) : null;

    this.toggleSubmitButton(this.getCurrentStep(event), isValid);
  }

  onInputKeyPress(event) {
    if (!/^[a-zA-Z0-9\s]+$/.test(String.fromCharCode(event.which))) {
      event.preventDefault();
    }
  }

  toggleSubmitButton(currentStep, enable) {
    const submitButton = currentStep.querySelector("input[type='submit']");

    submitButton !== null ? submitButton.toggleAttribute('disabled', !enable) : null;
  }

  onRangeInputChange(event) {
    this.updateSliderProgress(event.target);
    // Keep quiz state in sync with the slider value (important for offer matching after back/forward).
    if (event?.target?.name) {
      this.quizInputs[event.target.name] = String(event.target.value);
      if (event.target.name === 'weight') {
        // Weight-derived helpers should be recalculated at the email step.
        delete this.quizInputs.weight_range;
        delete this.quizInputs.weight_label;
      }
    }
    this.toggleSubmitButton(this.getCurrentStep(event), true);
  }

  getThumbWidthPx(slider) {
    const cssVar = getComputedStyle(slider).getPropertyValue('--thumb-w').trim();
    const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    if (cssVar.endsWith('rem')) return parseFloat(cssVar) * rootPx;
    if (cssVar.endsWith('px')) return parseFloat(cssVar);
    return 6 * rootPx;
  }

  updateSliderProgress(slider) {
    const container = slider.closest('.quiz-step__input-range_slider-container');
    if (!container) return;

    const progressBar = container.querySelector('.quiz-step__input-range_progress');
    const display = container.querySelector('.quiz-step__input-range_display');
    const valueSpan = container.querySelector('.quiz-step__input-range_value');

    if (!progressBar || !display || !valueSpan) return;

    const min = parseFloat(slider.min);
    const max = parseFloat(slider.max);
    const value = parseFloat(slider.value);
    const t = (value - min) / (max - min);

    const sliderWidth = slider.getBoundingClientRect().width;
    const thumbW = this.getThumbWidthPx(slider);

    const thumbLeftPx = t * (sliderWidth - thumbW);

    progressBar.style.width = thumbLeftPx + 'px';
    valueSpan.textContent = value;
    display.style.left = thumbLeftPx + thumbW / 2 + 'px';
  }

  initializeSliders() {
    this.querySelectorAll('.quiz-step__input[type="range"]').forEach((slider) => {
      this.updateSliderProgress(slider);
    });
  }

  onFormSubmit(event) {
    event.preventDefault();

    const form = event.target.closest('form');
    const formData = new FormData(form);
    const formDataJson = {};
    const currentStep = this.getCurrentStep(event);

    formData.forEach((value, key) => {
      formDataJson[key] = formDataJson[key] ? [].concat(formDataJson[key], value) : value;
    });

    if (Object.keys(formDataJson)[0] === 'name') {
      this.updateDogName(formDataJson.name);
    }

    // If this is the email step, calculate weight range before proceeding
    if (formDataJson.email && this.quizInputs.weight) {
      const weightRangeData = this.getWeightRangeForKlaviyo();
      if (weightRangeData) {
        this.quizInputs.weight_range = weightRangeData.weightRange;
        this.quizInputs.weight_label = weightRangeData.weightLabel;
      }
    }

    Object.assign(this.quizInputs, formDataJson);

    // Send Submit Quiz event to Klaviyo when email step is submitted
    if (formDataJson.email) {
      // Build profile data with quiz answers as custom properties
      const profileData = { $email: formDataJson.email };

      // Add quiz answers as custom properties with dog1_ prefix
      Object.keys(this.quizInputs).forEach((key) => {
        // Skip internal/helper keys and email
        if (key === 'email' || key === 'weight_range' || key === 'weight_label') {
          return;
        }
        // Use weight label instead of raw weight number
        if (key === 'weight') {
          profileData[`dog1_${key}`] =
            this.quizInputs.weight_label || this.quizInputs.weight_range || this.quizInputs[key];
        } else {
          profileData[`dog1_${key}`] = this.quizInputs[key];
        }
      });

      if (typeof klaviyo !== 'undefined') {
        klaviyo.push(['identify', profileData]);
      }
      this.sendSubmitQuizEvent();

      // Subscribe to Klaviyo list immediately when email is submitted
      if (this.klaviyoListID) {
        this.sendDataToKlaviyo('email_submission');
      }
    }

    this.navigateStep(currentStep, 'next');
  }

  getWeightRangeForKlaviyo() {
    this.syncWeightFromDOM();
    const weight = parseInt(this.quizInputs.weight, 10);
    if (isNaN(weight)) return null;

    // Get the offer step containing all offer blocks
    const offerStep = this.querySelector('quiz-step[quiz-step-index="offer"]');
    if (!offerStep) return null;

    // Get all offer blocks and find matching weight range
    const offerBlocks = offerStep.querySelectorAll('.quiz-offer-block');

    for (const offerBlock of offerBlocks) {
      const weightRanges = (offerBlock.dataset.weightRanges || '').split(',').filter(Boolean);
      const weightLabels = (offerBlock.dataset.weightLabels || '').split(',').filter(Boolean);

      for (let i = 0; i < weightRanges.length; i++) {
        if (this.isWeightInRange(weight, weightRanges[i])) {
          return {
            weightRange: weightRanges[i],
            weightLabel: weightLabels[i] || weightRanges[i],
          };
        }
      }
    }

    return null;
  }

  getMatchingOfferInfo() {
    // Find matching offer block based on display conditions and weight
    const matchingOfferBlock = this.findMatchingOfferBlock();

    if (!matchingOfferBlock) {
      return null;
    }

    // Get variant data from embedded JSON
    const variantsScript = matchingOfferBlock.querySelector('.offer-variants-data');
    let variants = [];
    if (variantsScript) {
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        console.error('Failed to parse variants data:', e);
      }
    }

    // Get matching weight index and variant info
    const matchingIndex = this.getOfferBlockMatchingIndex(matchingOfferBlock);
    matchingOfferBlock.dataset.matchingWeightIndex = String(matchingIndex);
    const variantIds = (matchingOfferBlock.dataset.variantIds || '').split(',').filter(Boolean);
    const variantId = variantIds[matchingIndex];
    const variant = variants.find((v) => v.id.toString() === variantId?.toString() || v.id === parseInt(variantId, 10));

    // Get discount info
    const discountPercent = parseInt(matchingOfferBlock.dataset.discountPercent || '0', 10);
    const originalPrice = variant ? variant.price / 100 : 0;
    const discountedPrice =
      discountPercent > 0 ? Math.round(originalPrice * (1 - discountPercent / 100) * 100) / 100 : originalPrice;

    // Use variant image if available, fallback to product image
    const variantImageUrl = variant?.image_url || matchingOfferBlock.dataset.productImageUrl || '';

    return {
      productId: parseInt(matchingOfferBlock.dataset.productId, 10) || null,
      productTitle: matchingOfferBlock.dataset.productTitle || '',
      productUrl: matchingOfferBlock.dataset.productUrl || '',
      productImageUrl: variantImageUrl,
      originalPrice: originalPrice,
      discountedPrice: discountedPrice,
      discountPercent: discountPercent,
    };
  }

  async sendSubmitQuizEvent() {
    if (!this.enableBuiltInOffer) {
      return;
    }

    const offerInfo = this.getMatchingOfferInfo();

    if (!offerInfo || !offerInfo.productId) {
      console.warn('No matching offer found for Submit Quiz event');
      return;
    }

    // Build the event payload
    const eventPayload = {
      // Must have fields
      quiz_name: this.quizName,
      result_name: offerInfo.productTitle,
      result_image: offerInfo.productImageUrl,
      result_URL: offerInfo.productUrl,
      result_id: offerInfo.productId,

      // Nice to have fields
      result_currency_symbol: '$',
      result_price: offerInfo.originalPrice,
      result_discounted_price: offerInfo.discountedPrice,
    };

    // Add quiz answers to the event payload
    Object.keys(this.quizInputs).forEach((key) => {
      // Skip internal/helper keys
      if (key === 'weight_range' || key === 'weight_label') {
        return;
      }
      // Use weight label instead of raw weight number
      if (key === 'weight') {
        eventPayload[`answer_${key}`] =
          this.quizInputs.weight_label || this.quizInputs.weight_range || this.quizInputs[key];
      } else {
        // Add quiz answers with answer_ prefix to avoid conflicts with quiz_name
        eventPayload[`answer_${key}`] = this.quizInputs[key];
      }
    });

    // Send the event to Klaviyo
    try {
      if (typeof klaviyo !== 'undefined') {
        klaviyo.push(['track', 'Submit Quiz', eventPayload]);
      }
    } catch (error) {
      console.error('Error sending Submit Quiz event to Klaviyo:', error);
    }
  }

  navigateStep(currentStep, direction) {
    const targetStep = direction === 'next' ? currentStep.nextElementSibling : currentStep.previousElementSibling;
    const targetIndex = targetStep.getAttribute('quiz-step-index');
    const url = new URL(window.location);

    currentStep.style.display = 'none';
    targetStep.style.display = 'flex';

    if (targetIndex !== 'last') {
      url.searchParams.set('step', targetIndex);
      history.pushState({ step: targetIndex }, '', url);
    }

    this.animateProgressBar(targetIndex);
    this.animateElementBlocks(targetStep);

    if (targetIndex == 0) {
      sessionStorage.setItem('offerLandingVisited', false);
    }

    targetIndex === 'last' && this.quizFinalStep(targetStep);
  }

  historyNavigateStep() {
    let params = new URLSearchParams(window.location.search);
    const targetIndex = params.get('step') || 0;

    // Hide all steps first
    document.querySelectorAll('quiz-step').forEach((el) => {
      el.style.display = 'none';
    });

    // Also hide all offer blocks
    document.querySelectorAll('.quiz-offer-block').forEach((el) => {
      el.style.display = 'none';
    });

    // Handle offer step specially
    if (targetIndex === 'offer' && this.enableBuiltInOffer) {
      const offerStep = this.querySelector('quiz-step[quiz-step-index="offer"]');
      if (offerStep) {
        offerStep.style.display = 'flex';

        // When returning to offer step via browser history, re-process matching based on latest inputs.
        const matchedOfferBlock = this.processBuiltInOfferOnReturn();

        // Fallback: if we couldn't match anything, show last shown offer (legacy behavior)
        if (!matchedOfferBlock && this.lastShownOfferHandle) {
          const offerBlock = offerStep.querySelector(
            `.quiz-offer-block[data-offer-handle="${this.lastShownOfferHandle}"]`
          );
          if (offerBlock) {
            offerBlock.style.display = 'block';
          }
          this.reEnableOfferCTAButtons();
        }
        return;
      }
    }

    // Handle 'last' step - show loading step
    if (targetIndex === 'last') {
      const lastStep = this.querySelector("quiz-step[quiz-step-index='last']");
      if (lastStep) {
        lastStep.style.display = 'flex';
        // Reset loading progress
        const progress = lastStep.querySelector('[loading-screen-progress]');
        if (progress) {
          progress.style.width = '0%';
        }
      }
      return;
    }

    // Handle regular steps
    const targetStep = this.querySelector("quiz-step[quiz-step-index='" + targetIndex + "']");
    if (targetStep) {
      targetStep.style.display = 'flex';
      this.animateProgressBar(targetIndex);
      this.animateElementBlocks(targetStep);
    }

    if (targetIndex == 0) {
      sessionStorage.setItem('offerLandingVisited', false);
    }
  }

  onBackClick(event) {
    // Check if we're currently on the offer step
    const currentUrl = new URL(window.location);
    const currentStep = currentUrl.searchParams.get('step');

    if (currentStep === 'offer') {
      // Going back from offer step - don't reset, just let history handle it
      this.historyNavigateStep();
      return;
    }

    // Reset offer step state when going back from other steps
    this.resetOfferStep();

    if (event) {
      this.navigateStep(this.getCurrentStep(event), 'prev');
    } else {
      this.historyNavigateStep();
    }
  }

  updateDogName(dogName) {
    const dogNameElement = this.querySelectorAll('[dog-name]');
    const dogNameFormatted = dogName.replace(/^(\w)(.+)/, (match, p1, p2) => p1.toUpperCase() + p2.toLowerCase());

    dogNameElement.forEach((el) => (el.textContent = dogNameFormatted));
  }

  animateElementBlocks(targetStep) {
    const animatedBlocks = targetStep.querySelectorAll('[animate-fade-in]');
    const delay = 200;

    animatedBlocks.forEach((block, index) => {
      setTimeout(() => {
        block.removeAttribute('animate-fade-in');
      }, index * delay);
    });

    // Initialize range sliders after step is visible
    setTimeout(() => {
      const rangeInputs = targetStep.querySelectorAll('.quiz-step__input[type="range"]');
      rangeInputs.forEach((slider) => this.updateSliderProgress(slider));
    }, 50);
  }

  animateProgressBar(targetIndex) {
    const progressBar = this.querySelector('quiz-progress');
    const progressBarInner = progressBar.querySelector('[quiz-progress-inner]');
    const progressBarText = progressBar.querySelector('[quiz-progress-text]');

    if (targetIndex !== 'last' && targetIndex !== 'offer') {
      const quizCount = this.querySelectorAll('quiz-step').length - 2; //subtract email step and final step
      const currentProgress = Math.round((100 / quizCount) * Number(targetIndex)) + '%';

      progressBar.style.display = progressBar.style.display === 'none' ? 'block' : progressBar.style.display;
      window.requestAnimationFrame(function () {
        progressBarInner.style.width = currentProgress;
        progressBarText.textContent = currentProgress;
      });
    } else {
      progressBar.style.display = 'none';
    }
  }

  quizFinalStep(targetStep) {
    const loadingScreenProgress = targetStep.querySelector('[loading-screen-progress]');

    // Reset offer step state before proceeding
    this.resetOfferStep();

    // Only push 'last' step to history if built-in offer is NOT enabled
    // When built-in offer is enabled, we skip 'last' in history so back from offer goes to last quiz step
    if (!this.enableBuiltInOffer) {
      const url = new URL(window.location);
      url.searchParams.set('step', 'last');
      history.pushState({ step: 'last' }, '', url);
    }

    // Use { once: true } to prevent duplicate event handlers
    const handleTransitionEnd = () => {
      if (this.enableBuiltInOffer) {
        this.processBuiltInOffer(targetStep);
      } else {
        this.redirectPage();
      }
    };

    loadingScreenProgress.addEventListener('transitionend', handleTransitionEnd, { once: true });
    window.requestAnimationFrame(() => (loadingScreenProgress.style.width = '100%'));
  }

  resetOfferStep() {
    // Hide offer step
    const offerStep = this.querySelector('quiz-step[quiz-step-index="offer"]');
    if (offerStep) {
      offerStep.style.display = 'none';

      // Hide all offer blocks
      const offerBlocks = offerStep.querySelectorAll('.quiz-offer-block');
      offerBlocks.forEach((block) => {
        block.style.display = 'none';
        // Reset matching weight index
        delete block.dataset.matchingWeightIndex;
      });
    }

    // Reset loading progress
    const loadingStep = this.querySelector("quiz-step[quiz-step-index='last']");
    if (loadingStep) {
      const progress = loadingStep.querySelector('[loading-screen-progress]');
      if (progress) {
        progress.style.width = '0%';
      }
    }
  }

  processBuiltInOfferOnReturn() {
    // Hide progress bar on offer step
    const progressBar = this.querySelector('quiz-progress');
    if (progressBar) {
      progressBar.style.display = 'none';
    }

    // Find matching offer block based on display conditions and weight (for returning from checkout)
    const matchingOfferBlock = this.findMatchingOfferBlock();

    if (matchingOfferBlock) {
      // Show only the matching offer block
      matchingOfferBlock.style.display = 'block';

      // Store the shown offer block handle for history navigation
      this.lastShownOfferHandle = matchingOfferBlock.dataset.offerHandle;

      // Get variant data and populate prices
      const matchingIndex = this.getOfferBlockMatchingIndex(matchingOfferBlock);
      matchingOfferBlock.dataset.matchingWeightIndex = String(matchingIndex);
      const variantIds = (matchingOfferBlock.dataset.variantIds || '').split(',').filter(Boolean);
      const quantities = (matchingOfferBlock.dataset.quantities || '').split(',').filter(Boolean);
      const weightLabels = (matchingOfferBlock.dataset.weightLabels || '').split(',').filter(Boolean);
      const intervalsDays = (matchingOfferBlock.dataset.intervalsDays || '').split(',').filter(Boolean);
      const sellingPlanIds = (matchingOfferBlock.dataset.sellingPlanIds || '').split(',').filter(Boolean);

      // Get variant data from embedded JSON
      const variantsScript = matchingOfferBlock.querySelector('.offer-variants-data');
      let variants = [];
      if (variantsScript) {
        try {
          variants = JSON.parse(variantsScript.textContent);
        } catch (e) {
          console.error('Failed to parse variants data:', e);
        }
      }

      const variantId = variantIds[matchingIndex];
      const quantity = parseInt(quantities[matchingIndex] || '1', 10);
      const weightLabel = weightLabels[matchingIndex] || '';
      const intervalDays = intervalsDays[matchingIndex] || '';
      const sellingPlanId = sellingPlanIds[matchingIndex] || '';
      const variant = variants.find(
        (v) => v.id.toString() === variantId?.toString() || v.id === parseInt(variantId, 10)
      );

      // Update dynamic content
      this.updateOfferDynamicContent(matchingOfferBlock, variant, weightLabel, intervalDays, quantity);

      // Setup CTA click handler
      const ctaBtn = matchingOfferBlock.querySelector('[data-offer-cta]');
      if (ctaBtn && variantId) {
        const newCtaBtn = ctaBtn.cloneNode(true);
        ctaBtn.parentNode.replaceChild(newCtaBtn, ctaBtn);
        newCtaBtn.addEventListener('click', () => this.handleOfferCTA(variantId, quantity, sellingPlanId, weightLabel));
      }

      // Re-enable CTA buttons
      this.reEnableOfferCTAButtons();
      return matchingOfferBlock;
    }
    return null;
  }

  processBuiltInOffer(loadingStep) {
    // Find matching offer block based on display conditions and weight
    const matchingOfferBlock = this.findMatchingOfferBlock();

    if (matchingOfferBlock) {
      this.showOfferStep(loadingStep, matchingOfferBlock);
      return;
    }

    // Fallback to redirect if no matching offer found
    this.redirectPage();
  }

  findMatchingOfferBlock() {
    this.syncWeightFromDOM();
    // Get the offer step containing all offer blocks
    const offerStep = this.querySelector('quiz-step[quiz-step-index="offer"]');
    if (!offerStep) return null;

    // Get all offer blocks within the step
    const offerBlocks = offerStep.querySelectorAll('.quiz-offer-block');

    // Fallback: first offer block whose display conditions match.
    // Use only if we can't find a weight-specific match.
    let fallbackOfferBlock = null;

    for (const offerBlock of offerBlocks) {
      // Get display conditions from embedded script tag
      const conditionsScript = offerBlock.querySelector('.offer-display-conditions');
      if (!conditionsScript) continue;

      let conditions;
      try {
        conditions = JSON.parse(conditionsScript.textContent);
      } catch (e) {
        console.error('Failed to parse display_conditions:', e);
        continue;
      }

      // Check if display conditions match
      const conditionsMatch = this.evaluateDisplayConditions(conditions);
      if (!conditionsMatch) continue;

      if (!fallbackOfferBlock) fallbackOfferBlock = offerBlock;

      // Check if weight is in range for this offer
      const weightRanges = (offerBlock.dataset.weightRanges || '').split(',').filter(Boolean);
      const weight = parseInt(this.quizInputs.weight, 10);

      // If no weight ranges defined, don't immediately return; keep as fallback and keep searching.
      if (weightRanges.length === 0) continue;
      // If weight missing/invalid, we can't do weight matching; fall back later.
      if (isNaN(weight)) continue;

      // Find matching weight range index
      for (let i = 0; i < weightRanges.length; i++) {
        if (this.isWeightInRange(weight, weightRanges[i])) {
          // Store the matching index for price/variant lookup
          offerBlock.dataset.matchingWeightIndex = i;
          return offerBlock;
        }
      }
    }

    return fallbackOfferBlock;
  }

  evaluateDisplayConditions(conditions) {
    const { conditions_met, step_answer_pairs } = conditions;

    if (!step_answer_pairs || step_answer_pairs.length === 0) {
      return true;
    }

    const results = step_answer_pairs.map((pair) => {
      const userAnswer = this.quizInputs[pair.step_name];
      if (!userAnswer) return false;

      const normalizedUserAnswer = userAnswer.toLowerCase().trim();
      const normalizedAnswersList = pair.answers_list.map((a) => a.toLowerCase().trim());

      return normalizedAnswersList.includes(normalizedUserAnswer);
    });

    if (conditions_met === 'any') {
      return results.some((r) => r === true);
    } else {
      // 'all' condition
      return results.every((r) => r === true);
    }
  }

  isWeightInRange(weight, rangeStr) {
    // Handle formats like "0-20lbs", "20-30lbs", "100+", "100+lbs"
    const cleanRange = rangeStr.replace(/lbs/gi, '').trim();

    if (cleanRange.includes('+')) {
      const minWeight = parseInt(cleanRange.replace('+', ''), 10);
      return weight >= minWeight;
    }

    if (cleanRange.includes('-')) {
      const [min, max] = cleanRange.split('-').map((s) => parseInt(s.trim(), 10));
      return weight >= min && weight <= max;
    }

    return false;
  }

  showOfferStep(loadingStep, offerBlock) {
    // Get the parent offer step
    const offerStep = this.querySelector('quiz-step[quiz-step-index="offer"]');
    if (!offerStep) {
      this.redirectPage();
      return;
    }

    // Hide loading step, show offer step
    loadingStep.style.display = 'none';
    offerStep.style.display = 'flex';

    // Show only the matching offer block
    offerBlock.style.display = 'block';

    // Store the shown offer block handle for history navigation
    this.lastShownOfferHandle = offerBlock.dataset.offerHandle;

    // Update URL with offer step
    const url = new URL(window.location);
    url.searchParams.set('step', 'offer');
    history.pushState({ step: 'offer' }, '', url);

    // Get variant data and populate prices
    const matchingIndex = this.getOfferBlockMatchingIndex(offerBlock);
    offerBlock.dataset.matchingWeightIndex = String(matchingIndex);
    const variantIds = (offerBlock.dataset.variantIds || '').split(',').filter(Boolean);
    const quantities = (offerBlock.dataset.quantities || '').split(',').filter(Boolean);
    const weightLabels = (offerBlock.dataset.weightLabels || '').split(',').filter(Boolean);
    const intervalsDays = (offerBlock.dataset.intervalsDays || '').split(',').filter(Boolean);
    const sellingPlanIds = (offerBlock.dataset.sellingPlanIds || '').split(',').filter(Boolean);

    // Get variant data from embedded JSON
    const variantsScript = offerBlock.querySelector('.offer-variants-data');
    let variants = [];
    if (variantsScript) {
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        console.error('Failed to parse variants data:', e);
      }
    }

    const variantId = variantIds[matchingIndex];
    const quantity = parseInt(quantities[matchingIndex] || '1', 10);
    const weightLabel = weightLabels[matchingIndex] || '';
    const intervalDays = intervalsDays[matchingIndex] || '';
    const sellingPlanId = sellingPlanIds[matchingIndex] || '';
    const variant = variants.find((v) => v.id.toString() === variantId?.toString() || v.id === parseInt(variantId, 10));

    // Update dynamic content
    this.updateOfferDynamicContent(offerBlock, variant, weightLabel, intervalDays, quantity);

    // Setup CTA click handler (remove old handler first to prevent duplicates)
    const ctaBtn = offerBlock.querySelector('[data-offer-cta]');
    if (ctaBtn && variantId) {
      // Clone and replace to remove all existing event listeners
      const newCtaBtn = ctaBtn.cloneNode(true);
      ctaBtn.parentNode.replaceChild(newCtaBtn, ctaBtn);
      newCtaBtn.addEventListener('click', () => this.handleOfferCTA(variantId, quantity, sellingPlanId, weightLabel));
    }
  }

  updateOfferDynamicContent(offerBlock, variant, weightLabel, intervalDays, quantity = 1) {
    const dogName = this.quizInputs.name || 'Your dog';
    const dogNameFormatted = dogName.replace(/^(\w)(.+)/, (match, p1, p2) => p1.toUpperCase() + p2.toLowerCase());

    // Update subtitle - replace [dog_name] placeholder
    const subtitle = offerBlock.querySelector('[data-offer-subtitle]');
    if (subtitle) {
      // Subtitle contains HTML (including `<span class="js-offer-interval">…</span>`), so do NOT use textContent.
      // Cache a stable template that can't "bake in" a previously-rendered interval number.
      const templateHtmlRaw = subtitle.dataset.templateHtml || subtitle.innerHTML;
      const templateHtml = templateHtmlRaw.replace(
        /(<span[^>]*class="[^"]*js-offer-interval[^"]*"[^>]*>)([\s\S]*?)(<\/span>)/i,
        '$1interval$3'
      );
      subtitle.dataset.templateHtml = templateHtml;
      subtitle.innerHTML = templateHtml.replace(/\[dog_name\]/gi, dogNameFormatted + "'s");
    }

    // Update interval placeholders AFTER subtitle HTML render so it cannot be overwritten by re-rendering
    const intervalElements = offerBlock.querySelectorAll('.js-offer-interval');
    intervalElements.forEach((el) => {
      el.textContent = intervalDays || '30';
    });

    // Update weight placeholders - use exact weight user selected
    const weightElements = offerBlock.querySelectorAll('.js-offer-weight');
    weightElements.forEach((el) => {
      el.textContent = this.quizInputs.weight + 'lbs';
    });

    // Update title - replace [weight] placeholder
    const title = offerBlock.querySelector('[data-offer-title]');
    if (title) {
      // Cache original title template so repeated renders don't permanently remove placeholders
      const templateText = title.dataset.templateText || title.textContent;
      title.dataset.templateText = templateText;
      title.textContent = templateText.replace(/\[weight\]/gi, weightLabel || this.quizInputs.weight + 'lbs');
    }

    // Update prices based on discount (total price, including quantity)
    const priceBlock = offerBlock.querySelector('[data-offer-price]');
    const comparePriceBlock = offerBlock.querySelector('[data-offer-compare-price]');
    const discountPercent = parseInt(offerBlock.dataset.discountPercent || '0', 10);

    if (priceBlock && variant) {
      const safeQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      const originalPrice = variant.price * safeQuantity;

      if (discountPercent > 0) {
        // Discount > 0: Calculate discounted price and show original with strikethrough
        const discountedPrice = Math.round(originalPrice * (1 - discountPercent / 100));
        priceBlock.textContent = this.formatMoney(discountedPrice);
        if (comparePriceBlock) {
          comparePriceBlock.textContent = this.formatMoney(originalPrice);
          comparePriceBlock.style.display = 'inline';
        }
      } else {
        // No discount: Show only the regular price
        priceBlock.textContent = this.formatMoney(originalPrice);
        if (comparePriceBlock) {
          comparePriceBlock.style.display = 'none';
        }
      }
    }
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  async handleOfferCTA(variantId, quantity, sellingPlanId, weightLabel) {
    const ctaBtn = this.querySelector('[data-offer-cta]:not([disabled])');
    if (!ctaBtn) return; // Already processing or button not found

    ctaBtn.disabled = true;
    ctaBtn.classList.add('loading');

    try {
      // 1. Clear cart first
      await this.clearCart();

      // 2. Build order attributes from quiz answers
      const orderAttributes = this.getQuizOrderAttributes(weightLabel);

      if (this.quizTag) {
        orderAttributes['quiz-id'] = this.quizTag;
      }

      // 3. Update cart attributes
      await this.updateCartAttributes(orderAttributes);

      // 4. Add to cart with selling plan (subscription)
      const cartItem = {
        id: variantId,
        quantity: quantity,
      };

      // Add selling plan for subscription
      if (sellingPlanId) {
        cartItem.selling_plan = sellingPlanId;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartItem),
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      // Send data to Klaviyo if needed
      if (this.quizInputs.email) {
        await this.sendDataToKlaviyo('built_in_offer');
      }

      // Redirect to checkout
      window.location.href = '/checkout';
    } catch (error) {
      console.error('Error adding to cart:', error);
      ctaBtn.disabled = false;
      ctaBtn.classList.remove('loading');
    }
  }

  async clearCart() {
    const response = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to clear cart');
    }

    return response.json();
  }

  reEnableOfferCTAButtons() {
    // Re-enable any disabled CTA buttons (for when user returns via browser back button)
    const ctaButtons = this.querySelectorAll('[data-offer-cta]');
    ctaButtons.forEach((btn) => {
      btn.disabled = false;
      btn.classList.remove('loading');
    });
  }

  getQuizOrderAttributes(weightLabel) {
    const attributes = {};

    // Add all quiz inputs as order attributes with dog1_ prefix
    Object.keys(this.quizInputs).forEach((key) => {
      // Skip internal/helper keys
      if (key === 'email' || key === 'weight_range' || key === 'weight_label') {
        return;
      }

      // Use weight_range instead of raw weight
      if (key === 'weight') {
        attributes[`dog1_${key}`] = weightLabel || this.quizInputs.weight_range || this.quizInputs.weight;
      } else {
        attributes[`dog1_${key}`] = this.quizInputs[key];
      }
    });

    return attributes;
  }

  async updateCartAttributes(attributes) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ attributes }),
    });

    if (!response.ok) {
      throw new Error('Failed to update cart attributes');
    }

    return response.json();
  }

  redirectPage() {
    const fetch_url = this.fetchUrl;
    const customerEmail = this.quizInputs.email;

    if (fetch_url) {
      // Using AbortController for timeout management
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: customerEmail }),
        signal: controller.signal,
      };

      fetch(fetch_url, options)
        .then((response) => {
          clearTimeout(timeoutId); // Clear the timeout once the response is received
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then((hasPreviousPurchase) => {
          const sourceType = hasPreviousPurchase ? 'product_page' : 'full_price';
          return this.sendDataToKlaviyo(sourceType);
        })
        .then((redirectURL) => {
          sessionStorage.setItem('offerLandingVisited', false);
          location.href = redirectURL;
        })
        .catch((error) => {
          this.sendDataToKlaviyo(this.defaultRedirect).then(async (redirectURL) => {
            sessionStorage.setItem('offerLandingVisited', false);
            location.href = redirectURL;
          });
        });
    }
  }

  async sendDataToKlaviyo(sourceType) {
    klaviyo.push(['identify', { $email: this.quizInputs.email }]);

    let redirectURL = false;
    const autoSubscribeEmail = !!this.klaviyoListID;
    const KLdata = autoSubscribeEmail
      ? {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: this.quizInputs.email,
                  properties: {},
                },
              },
            },
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: this.klaviyoListID,
              },
            },
          },
        }
      : {
          type: 'profile',
          attributes: {
            email: this.quizInputs.email,
            properties: {},
          },
        };

    const KLdataAttributes = autoSubscribeEmail ? KLdata.attributes.profile.data.attributes : KLdata.attributes;
    const klaviyoAPIFetchURL = autoSubscribeEmail
      ? 'https://a.klaviyo.com/client/subscriptions?company_id=SQEMbF'
      : 'https://a.klaviyo.com/client/profiles/?company_id=SQEMbF';

    Object.keys(this.quizInputs).forEach((name) => {
      const fullName = `dog1_${name}`;

      if (name !== 'email' && name !== 'weight_label') {
        if (name === 'weight') {
          // Send weight label instead of raw weight number if available
          KLdataAttributes.properties[fullName] =
            this.quizInputs.weight_label || this.quizInputs.weight_range || this.quizInputs[name].replace(' ', '');
        } else if (name !== 'weight_range') {
          KLdataAttributes.properties[fullName] = this.quizInputs[name];
        }
      }
    });

    KLdataAttributes.properties['$source'] = 'full prize quiz';
    KLdataAttributes.properties['source_handle'] = 'full prize quiz';

    if (sourceType == 'full_price' && redirectURL != '') {
      KLdataAttributes.properties['abandoned_cart_redirect_url'] = redirectURL;
    }

    const KLoptions = {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        revision: '2024-10-15',
      },
      body: JSON.stringify({ data: KLdata }),
    };

    redirectURL = this.buildRedirectUrl(sourceType);

    await fetch(klaviyoAPIFetchURL, KLoptions).catch((err) => console.error(err));

    return redirectURL;
  }

  buildRedirectUrl(sourceType) {
    let redirectUrl = '';
    const userInputsJson = { ...this.quizInputs };
    const targetUrl = this.fullPriceUrl || '';

    const quizParams = Object.entries(userInputsJson)
      .filter(([key]) => key !== 'email' && key !== 'weight_range' && key !== 'weight_label')
      .map(([key, value], index) => {
        const separator = index === 0 && !targetUrl.includes('?') ? '?' : '&';

        value = key === 'weight' ? String(value).replace(/\s/g, '') : String(value).replace(/\s/g, '_');
        userInputsJson[key] = value;

        return `${separator}${encodeURIComponent('quiz_' + key)}=${encodeURIComponent(value)}`;
      })
      .join('');

    localStorage.setItem('quiz_data', JSON.stringify(userInputsJson));

    if (sourceType === 'full_price') {
      redirectUrl = targetUrl + quizParams;
    } else {
      const weight = this.quizInputs.weight;

      const dogSizeType =
        weight === '65-100 lbs' || weight === '100-120 lbs' || weight === 'Over 120 lbs' ? 'LB' : 'SB';

      redirectUrl = dogSizeType === 'SB' ? this.jcPdpSbUrl : this.jcPdpLbUrl;
    }

    return redirectUrl;
  }
}

customElements.define('main-quiz', Quiz);
